import { Eye } from "lucide-react"
import { SidebarTooltip } from "@/components/ui/sidebar-tooltip"
import { useTranslation } from "@/hooks/use-translation"
import type { CanvasStylesUnavailableReason } from "@/lib/canvas-project-styles"
import { cn } from "@/lib/utils"

type ProjectStylesToggleProps = {
  enabled: boolean
  checked: boolean
  unavailableReason: CanvasStylesUnavailableReason | null
  onToggle: () => void
  onView: () => void
  /** Блокирует свитч (нет прав или идёт генерация). Глаз просмотра остаётся. */
  disabled?: boolean
}

export function ProjectStylesToggle({
  enabled,
  checked,
  unavailableReason,
  onToggle,
  onView,
  disabled = false,
}: ProjectStylesToggleProps) {
  const { t } = useTranslation("workspace")
  const stylesOn = enabled && checked
  const switchLabel = enabled
    ? t("canvas.panel.useProjectStyles")
    : unavailableReason === "empty"
      ? t("canvas.panel.useProjectStylesEmpty")
      : t("canvas.panel.useProjectStylesDisabled")

  return (
    <div className="ml-auto flex shrink-0 items-center gap-0.5">
      {stylesOn ? (
        <SidebarTooltip label={t("canvas.panel.viewStyles")} side="top">
          <button
            type="button"
            onClick={onView}
            className="rounded-lg p-1.5 text-ui-text-secondary hover:bg-ui-surface-raised hover:text-ui-text-primary"
            aria-label={t("canvas.panel.viewStyles")}
          >
            <Eye className="h-4 w-4" />
          </button>
        </SidebarTooltip>
      ) : null}
      <SidebarTooltip label={switchLabel} side="top">
        <button
          type="button"
          role="switch"
          aria-checked={stylesOn}
          aria-label={switchLabel}
          onClick={onToggle}
          disabled={disabled || !enabled}
          className={cn(
            "inline-flex items-center rounded-lg px-2 py-1 transition-colors disabled:opacity-40",
            stylesOn ? "text-ui-text-primary" : "text-ui-text-secondary hover:text-ui-text-primary",
          )}
        >
          <span
            className={cn(
              "relative h-4 w-7 rounded-full transition-colors",
              stylesOn ? "bg-ui-accent" : "dark:bg-ui-surface-raised light:bg-clay-4",
            )}
          >
            <span
              className={cn(
                "absolute top-0.5 left-0.5 h-3 w-3 rounded-full bg-white shadow-sm transition-transform",
                stylesOn && "translate-x-3",
              )}
            />
          </span>
        </button>
      </SidebarTooltip>
    </div>
  )
}
